import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/alerts/api/config.ts";
import { getAlerts, type IGetAlertsInput } from "@/alerts/api/requests/machine-alerts.requests.ts";
import { useDateTimeRangeStore, type DateTimeRangeValue } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useAlertLiveStore } from "@/alerts/stores/use-alert-live-store.ts";
import { isAlertWithinRange } from "@/alerts/utils/alert-range.ts";
import type { Alert } from "@/alerts/types/machine-alert.ts";
import type { ApiResponse } from "@/shared/types/api.ts";
import type { Machine } from "@/machines/types/machine.ts";

/** Server-side alert filters applied to the location query. */
export interface LocationAlertFilters {
  type_in?: string[];
  label_in?: string[];
  min_confidence?: number;
  max_confidence?: number;
}

export interface LocationAlerts {
  /** All location alerts, newest first. */
  alerts: Alert[];
  /** device_mxid → machine name, for display. */
  machineNameByDevice: Record<string, string>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Page size of the ONE location-wide alert read. This replaces a fan-out that
 * capped each machine at 1000, so at the fleet sizes this page runs against
 * (~5 machines) 5000 is the strictly non-truncating equivalent — the combined
 * call can never return less than the per-machine fan-out it replaces, and the
 * worst-case payload is the same total. Beyond that, the page is ordered
 * newest-first server-side, so any truncation drops only the OLDEST alerts —
 * never the fresh arrivals the chime / new-alert flow depends on.
 */
const LOCATION_ALERTS_PAGE_SIZE = 5000;

/** Stable empty list, so a machine-less location doesn't churn consumers. */
const EMPTY_ALERTS: Alert[] = [];

/**
 * Location-aggregate alert feed — ONE `getAlerts` query scoped to every machine
 * at the location via the `device_mxids` param, ordered newest-first
 * server-side, then sorted by time desc client-side. Dates from the global
 * `useDateTimeRangeStore`. Pass `pollMs` to auto-refetch (e.g. the 10s polling
 * used in Alert-preview mode). Optional `filters` (type/label/confidence) are
 * applied server-side.
 *
 * Live delivery rides on this same read: when the page mounts the Firestore
 * channel (`useAlertsLive`), a hint re-reads this query immediately instead of
 * waiting for the next poll. Polling is never replaced — it is the fallback the
 * channel degrades to.
 */
export const useLocationAlerts = (
  machines: Machine[],
  pollMs?: number,
  filters?: LocationAlertFilters,
  /**
   * Explicit fetch range. Realtime mode MUST pass a window ending at "now" —
   * the global store's `toDate` is a frozen/persisted value, so a new alert
   * that fires past it would never be fetched (breaking the chime + auto-load).
   * Falls back to the global range when omitted (historical browsing).
   */
  range?: DateTimeRangeValue,
): LocationAlerts => {
  const { value } = useDateTimeRangeStore();
  const fromDate = range?.fromDate ?? value.fromDate;
  const toDate = range?.toDate ?? value.toDate;
  const deviceMxids = useMemo(
    () => machines.map((m) => m.device_mxid).filter(Boolean),
    [machines],
  );
  const machineNameByDevice = useMemo(() => {
    const map: Record<string, string> = {};
    machines.forEach((m) => {
      if (m.device_mxid) map[m.device_mxid] = m.name;
    });
    return map;
  }, [machines]);

  const hasDevices = deviceMxids.length > 0;

  const { data, status, refetch } = useQuery<ApiResponse<Alert[]>>({
    queryKey: [
      QUERY_KEYS.ALERTS,
      {
        device_mxids: deviceMxids,
        start_time: fromDate,
        end_time: toDate,
        ordering: "-timestamp",
        type_in: filters?.type_in,
        label_in: filters?.label_in,
        min_confidence: filters?.min_confidence,
        max_confidence: filters?.max_confidence,
      },
    ],
    queryFn: (): Promise<ApiResponse<Alert[]>> =>
      getAlerts({
        device_mxids: deviceMxids,
        start_time: fromDate,
        end_time: toDate,
        page: 1,
        itemsPerPage: LOCATION_ALERTS_PAGE_SIZE,
        // Server-side newest-first, so the page keeps the NEWEST alerts — the
        // notify/chime flow must never miss fresh arrivals.
        ordering: "-timestamp",
        ...(filters?.type_in?.length ? { type_in: filters.type_in } : {}),
        ...(filters?.label_in?.length ? { label_in: filters.label_in } : {}),
        ...(filters?.min_confidence !== undefined
          ? { min_confidence: filters.min_confidence }
          : {}),
        ...(filters?.max_confidence !== undefined
          ? { max_confidence: filters.max_confidence }
          : {}),
      } as IGetAlertsInput),
    enabled: hasDevices,
    refetchInterval: (pollMs ?? false) as number | false,
    // Keep polling while the tab is backgrounded — the whole workflow is
    // "map open in another tab, the new-alert chime pulls the operator back",
    // which React Query would otherwise silence by pausing the interval when
    // the tab is hidden. (Browsers still throttle hidden-tab timers, so the
    // ring can lag up to ~1 min, but it fires.)
    refetchIntervalInBackground: true,
  });

  // --- Live channel bridge ---------------------------------------------------
  // A Firestore hint carries alert IDs only — never an alert body (no timestamp,
  // no GPS, no status), so it cannot be merged into the list as a row. It is a
  // "something changed, re-read now" signal, and re-reading THIS query is what
  // makes live arrivals reach the feed, the markers, the card and the notify
  // store. Because live and polled alerts therefore share one source of truth,
  // an alert delivered both ways can only appear once, and the from→to bound
  // below (`isAlertWithinRange`) applies to a live arrival exactly as it does to
  // a polled one.
  //
  // Dedup at this layer: a hint id is acted on once. The store's map is
  // TTL-pruned and wiped on scope begin/end, so we diff against the PREVIOUS id
  // set — a prune or a reset can never look like a fresh hint and re-trigger a
  // fetch (which would resubscribe/refetch in a loop).
  const recentHints = useAlertLiveStore((state) => state.recentHints);
  const handledHintIdsRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    const ids = Object.keys(recentHints);
    const handled = handledHintIdsRef.current;
    handledHintIdsRef.current = new Set(ids);
    // First pass only seeds: hints recorded before this mount are already
    // covered by the initial read.
    if (handled == null || !hasDevices) return;
    if (!ids.some((id) => !handled.has(id))) return;
    void refetch();
  }, [recentHints, hasDevices, refetch]);

  // `filter` copies — `data.data` is React Query's cached array and must never
  // be mutated, so the sort below runs on the copy. React Query's structural
  // sharing keeps `data` referentially stable across polls that return identical
  // payloads, and the window Dates only change when the picker does, so this memo
  // (and the returned object below) keep a STABLE reference — the page's alert
  // effects depend on that identity.
  const alerts = useMemo(() => {
    const list = data?.data;
    if (!list || list.length === 0) return EMPTY_ALERTS;
    // Defensive bound: the request is already windowed, but this feed is polled
    // live and drives every alert surface on `/map` (markers, feed, card nav,
    // chime, auto-load), so nothing outside from→to may reach the UI.
    const inRange = list.filter((alert) => isAlertWithinRange(alert, fromDate, toDate));
    if (inRange.length === 0) return EMPTY_ALERTS;
    return inRange.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
  }, [data, fromDate, toDate]);

  return useMemo(
    () => ({
      alerts,
      machineNameByDevice,
      // A location with no machines issues no query at all — report settled/empty
      // rather than a permanent pending (the fan-out over zero machines did the
      // same, and the page's chime + auto-load effects bail while loading).
      isLoading: hasDevices && status === "pending",
      isError: hasDevices && status === "error",
    }),
    [alerts, machineNameByDevice, hasDevices, status],
  );
};
